import { ColDef, GridApi, ValueGetterParams } from 'ag-grid-community';
import { useAtomValue } from 'jotai';
import { get } from 'lodash-es';
import React, { createContext, PropsWithChildren, useContext, useEffect, useMemo, useState } from 'react';
import {
  FieldDefinition,
  FieldPrimitiveType,
} from '../../../../../data-models/field-definition/field-definition.schema';
import { IField } from '../../../../../data-models/field2.data-model';
import { COMPANY_VIEW_TYPE } from '../../../../../data-models/view-config.data-model';
import { ViewModel } from '../../../../../models/view.model';
import { customFieldsByIdMapAtom } from '../../../../../services/state/AdminPanel/CustomFieldsStateJ';
import { formulaColumnsReadyAtom } from '../../../../../services/state/AdminPanel/FormulaFieldsStateJ';
import { ColumnMeta } from '../../../../../types';
import { AgFormatterService } from '../../../../../util/ag-formatter-service';
import { StandardFormatterId } from '../../../../../util/formatter-service';
import { useColumns } from '../../../hooks/useColumns/useColumns';
import { useInitializeMetricsData } from '../../../hooks/useInitializeMetricsData';
import { createFundNameComparator } from '../../../providers/column-utils';
import { currentViewFieldDefinitions } from '../../../state/ViewStateJ';
import { getAggregationConfigs } from './AggregationConfigs';
import { buildHeaderTooltip } from './buildHeaderTooltip';
import { getCellRendererConfigs } from './CellRendererConfigs';
import { getFilterOptions } from './ColumnFilters';
import { getColumnWidth } from './ColumnWidth';
import { getFieldTooltipContent } from './field-tooltip-content';
import { PredefinedColumns } from './PredefinedColumns/PredefinedColumns';
import { getTypeBasedConfigs } from './TypeBasedConfigs/TypeBasedConfigs';
import { useColsWithCompanyData } from './useColsWithCompanyData';
import { useColsWithIds } from './useColsWithIds';

/** Fields of ViewModel read by columnsMetaToColumnsDef. */
export type ColumnDefViewFields = Pick<ViewModel, 'companyViewType' | 'isDefault'>;

interface ColumnsDefinitionContextInterface {
  columnsDef: ColDef[];
}

const ColumnsDefinitionContext = createContext<ColumnsDefinitionContextInterface | undefined>(undefined);

export function columnsMetaToColumnsDef(
  customFieldsById: Map<number, IField<unknown>>,
  columnsMeta?: ColumnMeta[],
  view?: ColumnDefViewFields,
  fieldDefinitionsByKey?: Map<string, FieldDefinition>
): ColDef[] {
  if (!columnsMeta) {
    return [];
  }

  return columnsMeta.map((cm) => {
    const predefinedDefinition = PredefinedColumns.getDefinition(cm.name, view?.companyViewType);
    if (predefinedDefinition) {
      return predefinedDefinition;
    }

    // Mapper rule: fd.key === cm.path || cm.name. Lookup may miss for
    // columns the BE list doesn't return (rare during atom hydration);
    // fall back to ColumnMeta-derived display values in that case.
    const columnKey = cm.path || cm.name;
    const fd = fieldDefinitionsByKey?.get(columnKey);

    let columnConfig: ColDef = {
      colId: String(cm.name),
      field: cm.path,
      headerName: fd?.displayName ?? cm.displayName,
      enableRowGroup: cm.groupable,
      sortable: cm.sortable,
      // Compose a multi-line definition (name/description/source/formula)
      // from the code-defined content registry, falling back to the
      // FieldDefinition/ColumnMeta description. Undefined when no definition
      // exists, so no tooltip shows for undefined columns.
      headerTooltip: buildHeaderTooltip({
        displayName: fd?.displayName ?? cm.displayName,
        fallbackDescription: fd?.description ?? cm.description,
        content: getFieldTooltipContent(columnKey),
      }),
      lockPinned: true,
    };

    // Display half: route formatter resolution through the presentation
    // registry so the renderer-id → formatter-id translation lives in one
    // place. The ag-grid value-formatter wrapping (group/agg null-render)
    // stays inside AgFormatterService where it already lives.
    const valueFormatter = fd
      ? AgFormatterService.get().getFormatterForId(fd.format as StandardFormatterId)
      : undefined;
    if (valueFormatter != null) {
      if (fd?.type === FieldPrimitiveType.string) {
        columnConfig.valueGetter = (params: ValueGetterParams) => {
          const rawValue = get(params.data, cm.path);
          return valueFormatter({ ...params, value: rawValue });
        };
      } else {
        columnConfig.valueFormatter = valueFormatter;
      }
    }

    const filter = getFilterOptions(cm);
    if (cm.filterable && filter) {
      columnConfig = {
        ...columnConfig,
        ...filter,
      };
    } else {
      columnConfig.filter = false;
    }

    if (cm.name === 'Company Name' && cm.path === 'company') {
      columnConfig.pinned = 'left';
      columnConfig.lockPinned = true;
      columnConfig.filter = 'agSetColumnFilter';
      columnConfig.enableRowGroup = true;
    }

    const typeBasedConfigs = getTypeBasedConfigs(cm);

    if (typeBasedConfigs) {
      columnConfig = {
        ...columnConfig,
        ...typeBasedConfigs,
      };
    }

    const aggregationConfigs = getAggregationConfigs(cm);
    if (aggregationConfigs) {
      columnConfig = {
        ...columnConfig,
        ...aggregationConfigs,
      };
    }

    const width = getColumnWidth(cm);
    columnConfig = {
      ...columnConfig,
      ...width,
    };

    // Always-on view-derived rules (moved from ViewBasedConfigs.ts):
    // — Fund column gets a fund-name comparator everywhere
    // — In ROUND_TRACKER views, Fund also disables rowGroup and uses a custom valueGetter
    // — In ROUND_TRACKER default views, Company Name uses a set filter with quick-filter text
    if (view?.companyViewType === COMPANY_VIEW_TYPE.ROUND_TRACKER && cm.name === 'Fund') {
      columnConfig = {
        ...columnConfig,
        comparator: createFundNameComparator(),
        enableRowGroup: false,
        valueGetter: (params: ValueGetterParams) => {
          if (params.data?.fund) {
            return params.data.fund;
          } else if (!params.node?.group) {
            return '- ';
          }
        },
      };
    } else if (cm.name === 'Fund') {
      columnConfig = {
        ...columnConfig,
        comparator: createFundNameComparator(),
      };
    }

    if (
      view?.companyViewType === COMPANY_VIEW_TYPE.ROUND_TRACKER &&
      view?.isDefault &&
      cm.name === 'Company Name'
    ) {
      columnConfig = {
        ...columnConfig,
        filter: 'agSetColumnFilter',
        getQuickFilterText: (params) => params.value ?? '',
      };
    }

    const cellRendererConfigs = getCellRendererConfigs(cm, customFieldsById);
    if (cellRendererConfigs) {
      columnConfig = {
        ...columnConfig,
        ...cellRendererConfigs,
      };
    }

    return columnConfig;
  });
}

export const ColumnsDefinitionProvider: React.FC<PropsWithChildren<{ view: ViewModel }>> = ({
  children,
  view,
}) => {
  const columnsMeta = useColumns();
  const [columnApi, setColumnApi] = useState<GridApi | null>(null);
  const [columnsDef, setColumnsDef] = useState<ColDef[]>([]);
  const { getColsWithIds } = useColsWithIds();
  const { setColDefsForCompanyData: getColDefsForCompanyData } = useColsWithCompanyData();
  const { ready } = useInitializeMetricsData();
  const customFieldsById = useAtomValue(customFieldsByIdMapAtom);
  // FD-5049: formula columns are a third async source that useColumns cannot
  // report on — while the LD flag is unresolved its loadables read as "loaded,
  // none". Publishing columnsDef before they land opens SharedTable's
  // FD-2977 mount gate, ag-grid resolves initialState against a column set with
  // no formulaData.* colIds, and the view's saved order/visibility for them is
  // dropped; they then arrive as a live columnDefs update and maintainColumnOrder
  // appends them, visible.
  const formulaColumnsReady = useAtomValue(formulaColumnsReadyAtom);
  // Empty Map until the selected view and its columns hydrate; the mapper
  // handles the missing-FD-by-key case by falling back to ColumnMeta
  // values. Once hydrated, `columnsMetaToColumnsDef` reads display fields
  // through FieldDefinition + presentation helpers.
  const fieldDefinitionsByKey = useAtomValue(currentViewFieldDefinitions);

  // FD-2976: depend on the specific view fields the body actually reads
  // (companyViewType / isDefault for the always-on ColDef rules) instead
  // of the whole `view`. The latter changes reference on every `viewsPFJ`
  // write — including ones that touch only filteredFunds or gridState —
  // and would re-fire this effect for unrelated updates, pushing a fresh
  // columnsDef into AG Grid and triggering further column events.
  const viewCompanyViewType = view.companyViewType;
  const viewIsDefault = view.isDefault;
  useEffect(() => {
    if (!ready || !customFieldsById || !formulaColumnsReady) {
      return;
    }

    // FD-2976: build the narrowed view from the two tracked primitives so a
    // future change can't silently read another field of `view` (which is
    // closed over from render and would go stale against the dep array).
    const narrowedView: ColumnDefViewFields = {
      companyViewType: viewCompanyViewType,
      isDefault: viewIsDefault,
    };
    let apiColumnDef = columnsMetaToColumnsDef(
      customFieldsById,
      columnsMeta,
      narrowedView,
      fieldDefinitionsByKey
    );
    apiColumnDef = getColDefsForCompanyData(apiColumnDef, customFieldsById);
    apiColumnDef = getColsWithIds(apiColumnDef);

    setColumnsDef(apiColumnDef);
  }, [
    columnsMeta,
    viewCompanyViewType,
    viewIsDefault,
    ready,
    customFieldsById,
    formulaColumnsReady,
    fieldDefinitionsByKey,
    getColDefsForCompanyData,
    getColsWithIds,
  ]);

  const value = useMemo(() => ({ columnsDef, columnApi, setColumnApi }), [columnsDef, columnApi]);

  return <ColumnsDefinitionContext.Provider value={value}>{children}</ColumnsDefinitionContext.Provider>;
};

export const useColumnsDefinition = (): ColumnsDefinitionContextInterface => {
  const context = useContext(ColumnsDefinitionContext);

  if (typeof context === 'undefined') {
    throw new Error('useColumnsDef cannot be used outside ColumnsDefinitionProvider');
  }

  return context;
};
